/*
 * Responsive (stacked) layout overrides for a condition row, injected at
 * RUNTIME as a <style> tag rather than imported as a plain .css file.
 *
 * Why not a plain `import "./x.css"`: the production micro-frontend bundle
 * (esbuild module federation, build.js) emits a CSS import into a SEPARATE
 * css file that the host never <link>s -- so the rule silently vanished on
 * staging/prod (base inline layout everywhere) while the Vite dev server
 * injected it and worked. Everything else styles fine because it is
 * styled-components (runtime JS injection, bundler-agnostic).
 *
 * Why not styled-components: this project's styled-components (v5, bundled
 * stylis 3.5) does not understand the @container at-rule -- it drops the
 * nested class selectors and emits selector-less declarations the browser
 * discards.
 *
 * Injecting the raw text into a <style> tag is bundler-agnostic (it ships
 * inside the JS bundle as a string) and preserves @container verbatim. The
 * base (wide / inline) layout stays in styled-components (style.ts); these
 * rules only override it when the conditions container (container-name:
 * cl-conditions AND class cl-conditions-container -- EditAutomationPanelStyles)
 * is narrow. The `.cl-conditions-container` ancestor raises specificity above
 * the single-class styled-components base so these win regardless of
 * stylesheet insertion order.
 *
 * Breakpoint 600px: the conditions container ranges ~570px (settings panel at
 * its collapsed minimum, e.g. when a Conditional Logic block is present) up to
 * ~1000px+ (panel widened by shrinking the canvas). 600px sits inside that
 * range so the layout actually switches as the sidebar is resized: the narrow
 * branch panel stacks (Figma 19320), a widened panel goes inline (Figma 19111).
 */
export const CONDITIONS_RESPONSIVE_CSS = `
@container cl-conditions (max-width: 600px) {
  /* 7px gap between the WHERE/AND-OR column and the field column
     (Figma node 19320:194323). */
  .cl-conditions-container .cl-condition-section {
    margin-left: 7px;
  }

  /* Field + operator: each on its own full-width row instead of inline columns. */
  .cl-conditions-container .cl-condition-selector {
    display: block;
    width: 100%;
    margin-left: 0;
    margin-top: 8px;
  }

  /* The first control aligns with the WHERE/AND-OR chip (no top margin). */
  .cl-conditions-container .cl-condition-selector:first-child {
    margin-top: 0;
  }

  /* Value: also full-width on its own row. */
  .cl-conditions-container .cl-condition-argument {
    display: block;
    width: 100%;
    margin-left: 0;
    margin-top: 8px;
  }

  /* Field-missing error aligns under the field input (past WHERE + 7px gap). */
  .cl-conditions-container .cl-condition-error {
    padding-left: 87px;
  }

  /* Trash aligns to the last (value) row rather than the first
     (Figma node 19320:194345). */
  .cl-conditions-container .cl-condition-trash {
    align-self: flex-end;
  }
}
`;

const STYLE_ELEMENT_ID = "cl-conditions-responsive";

/**
 * Injects CONDITIONS_RESPONSIVE_CSS into <head> once. Idempotent (guarded by a
 * stable element id) and no-ops without a DOM, so repeated imports and
 * non-browser environments are safe.
 */
export const injectConditionsResponsiveStyles = (): void => {
  if (typeof document === "undefined") {
    return;
  }
  if (document.getElementById(STYLE_ELEMENT_ID) !== null) {
    return;
  }
  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = CONDITIONS_RESPONSIVE_CSS;
  document.head.appendChild(style);
};
